import { initializeApp, getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { FIREBASE_CONFIG as FIREBASE_CONFIG_IMPORT, API_URL } from '@/config';
import ApiService from '@/services/api.service';

// Получаем конфигурацию Firebase с fallback
let FIREBASE_CONFIG = FIREBASE_CONFIG_IMPORT;

// Fallback конфигурация на случай, если импорт не работает
if (!FIREBASE_CONFIG || !FIREBASE_CONFIG.apiKey) {
    console.warn('FIREBASE_CONFIG не загружен из config.js, используем fallback');
    FIREBASE_CONFIG = {
        apiKey: "AIzaSyC-2OGl_Wo9KuMYn2AcsooB5mU46ScSRZ0",
        authDomain: "testlubivpluse.firebaseapp.com",
        projectId: "testlubivpluse",
        storageBucket: "testlubivpluse.firebasestorage.app",
        messagingSenderId: "263082252225",
        appId: "1:263082252225:web:05aa183976ce4443724140",
        measurementId: "G-V1XBNDH6WT"
    };
}

class FirebaseService {
    constructor() {
        this.app = null;
        this.messaging = null;
        this.initialized = false;
    }

    async init() {
        if (this.initialized) {
            return;
        }

        // Инициализация Firebase не должна блокировать запуск приложения
        // Выполняем асинхронно без await
        this.initFirebase().catch((error) => {
            console.error('Ошибка инициализации Firebase (не критично):', error);
        });
    }

    async initFirebase() {
        try {
            // Проверяем, что конфигурация Firebase доступна
            if (!FIREBASE_CONFIG || !FIREBASE_CONFIG.apiKey) {
                console.warn('Firebase конфигурация не найдена, пропускаем инициализацию');
                return;
            }
            
            const firebaseConfig = FIREBASE_CONFIG;

            // Проверяем, не инициализирован ли Firebase уже
            try {
                // Пытаемся получить существующее приложение
                this.app = getApp();
                console.log('Используется существующее Firebase приложение');
            } catch (e) {
                // Если приложение не существует, создаем новое
                console.log('Инициализация нового Firebase приложения с конфигурацией:', {
                    apiKey: firebaseConfig.apiKey ? '***' + firebaseConfig.apiKey.slice(-4) : 'не указан',
                    projectId: firebaseConfig.projectId,
                    authDomain: firebaseConfig.authDomain
                });
                this.app = initializeApp(firebaseConfig);
            }
            
            // Проверяем поддержку Service Worker и браузера
            if ('serviceWorker' in navigator && 'Notification' in window) {
                this.messaging = getMessaging(this.app);
                this.initialized = true;
                
                // НЕ запрашиваем разрешение автоматически - только по действию пользователя
                // Разрешение будет запрошено при первом взаимодействии пользователя с уведомлениями
                
                // Получаем токен и регистрируем его (не блокируем)
                // Если разрешение уже есть, токен будет получен
                this.registerToken().catch((error) => {
                    console.warn('Не удалось зарегистрировать FCM токен:', error);
                });
                
                // Слушаем входящие сообщения когда приложение открыто
                this.setupMessageListener();
            } else {
                console.warn('Браузер не поддерживает push-уведомления');
            }
        } catch (error) {
            console.error('Ошибка инициализации Firebase:', error);
            // Не пробрасываем ошибку дальше, чтобы не сломать приложение
        }
    }

    async requestPermission() {
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                console.log('Разрешение на уведомления получено');
                return true;
            } else {
                console.log('Разрешение на уведомления отклонено');
                return false;
            }
        } catch (error) {
            console.error('Ошибка запроса разрешения:', error);
            return false;
        }
    }

    async registerToken() {
        try {
            if (!this.messaging) {
                return;
            }

            // Регистрируем Service Worker для получения уведомлений
            // Service Worker должен быть доступен по пути /static/firebase-messaging-sw.js
            let registration;
            try {
                registration = await navigator.serviceWorker.register('/static/firebase-messaging-sw.js');
            } catch (swError) {
                console.warn('Не удалось зарегистрировать Service Worker:', swError);
                // Если Service Worker не зарегистрирован, пробуем использовать существующий
                if (navigator.serviceWorker.controller) {
                    registration = await navigator.serviceWorker.getRegistration();
                } else {
                    // Если нет Service Worker, пропускаем регистрацию токена
                    console.warn('Service Worker недоступен, пропускаем регистрацию FCM токена');
                    return;
                }
            }
            
            if (!registration) {
                console.warn('Не удалось получить регистрацию Service Worker');
                return;
            }
            
            const token = await getToken(this.messaging, {
                vapidKey: process.env.VUE_APP_FIREBASE_VAPID_KEY || '',
                serviceWorkerRegistration: registration
            });

            if (token) {
                console.log('FCM токен получен:', token);
                // Отправляем токен на сервер
                await this.sendTokenToServer(token);
            } else {
                console.log('Не удалось получить FCM токен (возможно, нет VAPID ключа)');
            }
        } catch (error) {
            console.warn('Ошибка регистрации токена (не критично):', error);
            // Не пробрасываем ошибку, чтобы не сломать приложение
        }
    }

    async sendTokenToServer(token) {
        try {
            await ApiService.post('/fcm-token/', { token });
            console.log('FCM токен отправлен на сервер');
        } catch (error) {
            console.error('Ошибка отправки токена на сервер:', error);
        }
    }

    setupMessageListener() {
        if (!this.messaging) {
            return;
        }

        // Обработка сообщений когда приложение открыто
        onMessage(this.messaging, (payload) => {
            console.log('Получено push-уведомление:', payload);
            
            // Показываем уведомление
            if (payload.notification) {
                const notification = new Notification(payload.notification.title, {
                    body: payload.notification.body,
                    icon: payload.notification.icon || '/static/img/logo.png',
                    badge: '/static/img/logo.png',
                    tag: (payload.data && payload.data.event_id) || 'notification',
                    data: payload.data
                });

                // Обработка клика по уведомлению
                notification.onclick = (event) => {
                    event.preventDefault();
                    window.focus();
                    // Можно добавить навигацию на нужную страницу
                    if (payload.data && payload.data.url) {
                        window.location.href = payload.data.url;
                    }
                };
            }
        });
    }

    async updateToken() {
        if (this.initialized && this.messaging) {
            await this.registerToken();
        }
    }

    // Публичный метод для запроса разрешения на уведомления
    // Должен вызываться только по действию пользователя (клик на кнопку и т.д.)
    async requestNotificationPermission() {
        if (!this.initialized) {
            console.warn('Firebase не инициализирован');
            return false;
        }

        const granted = await this.requestPermission();
        if (granted) {
            // Если разрешение получено, регистрируем токен
            await this.registerToken();
        }
        return granted;
    }
}

export default new FirebaseService();
